/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { FunctionComponent, ReactNode, useState } from 'react';
import {
  EuiBreadcrumb,
  EuiButtonIcon,
  EuiFlyout,
  EuiFlyoutBody,
  EuiPageTemplate,
  euiPaletteForStatus,
  useIsWithinBreakpoints,
} from '@elastic/eui';
import { useRouter } from 'next/router';
import { css } from '@emotion/react';
import { useSession } from 'next-auth/react';
// import { tokens } from 'styles/tokens'; // re-add when the touch-target rule is re-enabled
import Spinner from '@components/spinner/spinner';
import Announcement from '@components/announcement';
import { useFeature } from '@lib/feature-toggles/useFeature';
import { FeatureFlag } from '@lib/feature-toggles/flags';
import { UiChromeProvider, useUiChrome } from './useUiChrome';
import { TopBar } from './TopBar';
import { Toolbar } from './Toolbar';

/**
 * Single fixed `EuiHeader` height. The TopBar is one header; when the
 * breadcrumb sub-header is shown a second header stacks below it, so the sticky
 * error banner clears `2 * HEADER_HEIGHT` (mirrors MainLayout's old 96/48 hard
 * codes, recomputed against the new chrome's header stack).
 */
const HEADER_HEIGHT = 48;

export type AppShellProps = {
  children: ReactNode;
  /**
   * Ad-hoc banner host slot. Renders below the announcement banners, between the
   * TopBar/sub-header and content.
   */
  banner?: ReactNode;
  breadcrumb?: EuiBreadcrumb[];
  pageTitle?: ReactNode;
  /** Content (Section) width. Outer template is capped separately for parity. */
  restrictWidth?: string | number | boolean;
  alignment?: 'top' | 'center' | 'horizontalCenter';
  panelled?: boolean;
  showSpinner?: boolean;
  sidebar?: ReactNode;
  sidebarWidth?: number;
  /**
   * When true the sidebar is ALWAYS rendered as a docked left
   * `EuiPageTemplate.Sidebar`, even on overlay/phone breakpoints (it never
   * routes to a flyout). Parity with MainLayout's `hideFlyout`.
   */
  hideFlyout?: boolean;
  /**
   * Top-bar action slot. Passed straight through to the TopBar, which renders
   * the actions inline on desktop and collapses them into an overflow menu on
   * phone/overlay breakpoints.
   */
  topBarActions?: ReactNode[];
};

/**
 * Scoped 44px minimum touch-target enforcement.
 *
 * SCOPED to the AppShell subtree ONLY — deliberately NOT promoted to
 * `global.styles.ts` / `eui-theme-overrides.ts`. Now that the chrome is
 * app-wide, every authed page renders inside this subtree, so the rule
 * applies app-wide in practice; promotion to a global override is deferred so
 * the scoping intent stays explicit.
 */
// DISABLED for now (per request): the scoped 44px touch-target rule made the
// toolbar action buttons noticeably taller than intended. To re-enable,
// uncomment this, re-add `...touchTargetCss` on the shell root <div>, and
// restore the `tokens` import.
/*
const touchTargetCss = {
  [[
    '.euiButton',
    '.euiButtonEmpty',
    '.euiButtonIcon',
    '.euiFieldText',
    '.euiFieldSearch',
    '.euiComboBox__inputWrap',
    '.euiSwitch',
    '.euiSelectableListItem',
    '.euiListGroupItem__button',
    '.euiHeaderSectionItemButton',
  ].join(', ')]: {
    minBlockSize: tokens.touch.min,
  },
} as const;
*/

/**
 * Inner shell — split out so it can consume `useUiChrome` (breakpoint + drawer
 * state) from context. `AppShell` wraps this in `UiChromeProvider`.
 */
const AppShellInner: FunctionComponent<AppShellProps> = ({
  children,
  banner,
  breadcrumb,
  pageTitle,
  restrictWidth = false,
  alignment = 'top',
  panelled = false,
  showSpinner = false,
  sidebar,
  sidebarWidth,
  hideFlyout = false,
  topBarActions,
}) => {
  const router = useRouter();
  const { data: session } = useSession();
  const { isOverlay, isPhone } = useUiChrome();
  const isMobile = useIsWithinBreakpoints(['xs', 's', 'm']);

  const hasAnnounceError = useFeature(FeatureFlag.AnnounceError);
  const hasAnnounceWarning = useFeature(FeatureFlag.AnnounceWarning);
  const hasAnnounceInfo = useFeature(FeatureFlag.AnnounceInfo);

  const [isSidebarFlyoutOpen, setSidebarFlyoutOpen] = useState(false);

  // The secondary toolbar (burger + breadcrumb + actions) shows on all app
  // surfaces; hidden only on the auth and error flows that have no chrome nav.
  const showToolbar =
    !router.route.startsWith('/auth') && router.route !== '/error';

  const palette = euiPaletteForStatus(5);

  // Sidebar routes to a RIGHT flyout only on overlay breakpoints AND when the
  // page hasn't opted out via hideFlyout. Otherwise it's a docked left sidebar.
  // (The LEFT side is owned by BurgerNav, so the page sidebar flyout is RIGHT.)
  const sidebarAsFlyout = !!sidebar && isOverlay && !hideFlyout;

  return (
    <div css={{ position: 'relative' }}>
      <Spinner show={showSpinner} />

      <EuiPageTemplate panelled={panelled} restrictWidth={true}>
        <TopBar />

        {/* Overlay sidebar toggle (filter icon), mirrors the old affordance. */}
        {sidebarAsFlyout && (
          <EuiButtonIcon
            aria-label="Toggle sidebar"
            color="primary"
            size="m"
            display="fill"
            style={{ margin: 16 }}
            iconType="filter"
            onClick={() => setSidebarFlyoutOpen(open => !open)}
          />
        )}

        {sidebar &&
          (sidebarAsFlyout ? (
            isSidebarFlyoutOpen && (
              <EuiFlyout
                side="right"
                size={isPhone ? 'l' : 's'}
                maxWidth={isPhone ? false : undefined}
                ownFocus
                onClose={() => setSidebarFlyoutOpen(false)}>
                <EuiFlyoutBody>{sidebar}</EuiFlyoutBody>
              </EuiFlyout>
            )
          ) : (
            <EuiPageTemplate.Sidebar
              minWidth={sidebarWidth ?? 400}
              sticky={true}
              paddingSize="s">
              {sidebar}
            </EuiPageTemplate.Sidebar>
          ))}

        {/* ---- Banner host: flag-driven announcements + ad-hoc banner ---- */}
        {hasAnnounceError && session?.announceError && (
          <div
            style={{
              position: 'sticky',
              top: `${HEADER_HEIGHT}px`,
              zIndex: 999,
            }}>
            <Announcement
              backgroundColor={palette[4]}
              textColor="white"
              message={session.announceError}
            />
          </div>
        )}

        {hasAnnounceWarning && session?.announceWarning && (
          <Announcement
            backgroundColor={palette[2]}
            textColor="black"
            message={session.announceWarning}
          />
        )}

        {hasAnnounceInfo && session?.announceInfo && (
          <Announcement
            backgroundColor={palette[1]}
            textColor="black"
            message={session.announceInfo}
          />
        )}

        {banner ? <div data-test-subj="app-shell-banner">{banner}</div> : null}

        <EuiPageTemplate.Section
          grow={true}
          paddingSize="m"
          restrictWidth={restrictWidth}
          alignment={alignment}
          css={css`
            .eui-15sbasf-euiPageSection__content-m {
              height: ${isMobile ? 'calc(100vh - 220px)' : '100%'};
            }
          `}>
          {showToolbar && (
            <Toolbar breadcrumb={breadcrumb} actions={topBarActions} />
          )}
          {pageTitle}
          {children}
        </EuiPageTemplate.Section>
      </EuiPageTemplate>
    </div>
  );
};

/**
 * Foundation chrome shell, app-wide: TopBar (BurgerNav + UserMenu)
 * + breadcrumb sub-header + banner host + optional left sidebar (decision (b):
 * docked left on desktop, RIGHT flyout on overlay) + content slot, on
 * `EuiPageTemplate`. Wrapped in `UiChromeProvider` so the two-drawer rule and
 * the single chrome breakpoint are in scope for everything inside.
 *
 * `MainLayout` is now a thin adapter that delegates to this component, so every
 * page that renders `<MainLayout>` gets this chrome.
 */
export const AppShell: FunctionComponent<AppShellProps> = props => {
  return (
    <UiChromeProvider>
      <AppShellInner {...props} />
    </UiChromeProvider>
  );
};

export default AppShell;
